import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, Input, Segmented, Space, Table, Tag, Tooltip, Typography, message } from 'antd';
import { MailOutlined, ReloadOutlined } from '@ant-design/icons';
import apiClient from '../../../shared/services/apiClient';
import { adminPath } from '../../../shared/config/paths';
import { formatDateTime, daysUntil } from '../../../shared/utils/date';

const { Text } = Typography;

const api = async (p) => {
    try {
        return (await p).data;
    } catch (e) {
        throw e.response?.data || e;
    }
};

/** 注册人邮箱验证：待验证 / 验证失败的域名，按截止时间排序。 */
const RegistrantVerification = () => {
    const [status, setStatus] = useState('all');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [data, setData] = useState({ items: [], total: 0, counts: { pending: 0, failed: 0 } });
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api(apiClient.get('/api/admin/domains/verification', { params: { status, search, page, page_size: 20 } }));
            if (r?.success) setData(r.data);
        } catch (e) {
            message.error(e.error || e.message || '加载失败');
        } finally {
            setLoading(false);
        }
    }, [status, search, page]);

    useEffect(() => { load(); }, [load]);

    const act = async (kind, id) => {
        setBusy(`${kind}:${id}`);
        try {
            const r = await api(apiClient.post(`/api/admin/domains/verification/${kind}`, { id }));
            if (r?.success) {
                message.success(kind === 'resend' ? '验证邮件已发送' : '已刷新');
                load();
            } else {
                message.error(r?.error || '操作失败');
            }
        } catch (e) {
            message.error(e.error || e.message || '操作失败');
        } finally {
            setBusy('');
        }
    };

    const columns = [
        {
            title: '域名', dataIndex: 'domain',
            render: (d, r) => <Link to={adminPath(`/domain-registration/detail/${r.id}`)}>{r.domain_unicode || d}</Link>,
        },
        { title: '用户', key: 'user', render: (_, r) => <span>{r.username || `#${r.user_id}`}{r.user_email ? <Text type="secondary"> · {r.user_email}</Text> : null}</span> },
        { title: '注册商', dataIndex: 'registrar_name', render: (v) => v || '-' },
        {
            title: '状态', dataIndex: 'registrant_verification', width: 100,
            render: (s) => (s === 'failed' ? <Tag color="error">验证失败</Tag> : <Tag color="warning">待验证</Tag>),
        },
        {
            title: '截止', dataIndex: 'registrant_verify_deadline', width: 190,
            render: (v) => {
                if (!v) return <Text type="secondary">-</Text>;
                const d = daysUntil(v);
                return <span style={{ color: d !== null && d <= 3 ? '#ff4d4f' : undefined }}>{formatDateTime(v)}{d !== null && d >= 0 ? ` · ${d} 天` : ''}</span>;
            },
        },
        { title: '上次检查', dataIndex: 'registrant_verify_checked_at', width: 160, render: (v) => (v ? formatDateTime(v) : '-') },
        {
            title: '', key: 'a', width: 90,
            render: (_, r) => (
                <Space size={0}>
                    <Tooltip title="重发验证邮件">
                        <Button size="small" type="text" icon={<MailOutlined />} disabled={r.status !== 'active'} loading={busy === `resend:${r.id}`} onClick={() => act('resend', r.id)} />
                    </Tooltip>
                    <Tooltip title="向注册商重新查询">
                        <Button size="small" type="text" icon={<ReloadOutlined />} loading={busy === `check:${r.id}`} onClick={() => act('check', r.id)} />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <Card
            title="注册人验证"
            bordered={false}
            extra={<Button icon={<ReloadOutlined />} onClick={load} loading={loading} />}
        >
            <Space wrap style={{ marginBottom: 12 }}>
                <Segmented
                    value={status}
                    onChange={(v) => { setStatus(v); setPage(1); }}
                    options={[
                        { value: 'all', label: `全部 ${data.counts.pending + data.counts.failed}` },
                        { value: 'pending', label: `待验证 ${data.counts.pending}` },
                        { value: 'failed', label: `验证失败 ${data.counts.failed}` },
                    ]}
                />
                <Input.Search allowClear placeholder="域名 / 用户" style={{ width: 220 }} onSearch={(v) => { setSearch(v); setPage(1); }} />
            </Space>
            <Table
                size="small"
                rowKey="id"
                loading={loading}
                dataSource={data.items}
                columns={columns}
                scroll={{ x: 900 }}
                pagination={{ current: page, pageSize: 20, total: data.total, onChange: setPage, showSizeChanger: false }}
            />
        </Card>
    );
};

export default RegistrantVerification;
